import { LitElement, html, css } from 'lit';
import {
  publishDraftPage, publishDraftSiteSettings, fetchDraftSitemap, fetchPageHistory,
  fetchSiteHistory, resetPageDraft, resetSiteDraft, restorePageVersion,
  restoreSiteVersion, republishPageVersion, republishSiteVersion,
  fetchTemplateStatus, fetchTemplateHistory, resetTemplateDrafts,
  restoreTemplateRelease, publishTemplateDrafts, republishTemplateRelease,
} from '../lib/api.js';
import { withBuildPrefix } from '../lib/build-paths.js';

export class SiteDraftPanel extends LitElement {
  static properties = {
    slug: { type: String },
    pageId: { type: Number },
    pageTitle: { type: String },
    pageChanged: { type: Boolean },
    settingsChanged: { type: Boolean },
    pageReady: { type: Boolean },
    _confirmTarget: { state: true },
    _busy: { state: true },
    _message: { state: true },
    _error: { state: true },
    _sitemap: { state: true },
    _history: { state: true },
    _target: { state: true },
    _hidden: { state: true },
    _templateStatus: { state: true },
  };

  static styles = css`
    :host { position: fixed; right: 1rem; bottom: 1rem; z-index: 10000; font-family: var(--font-sans, system-ui, sans-serif); color: #eef2ff; }
    .assembly { position: relative; transition: transform 300ms ease; }
    .assembly--hidden { transform: translateX(100%); }
    .widget-toggle { position: absolute; left: -2.6rem; top: .9rem; width: 2.6rem; height: 2.6rem; display: flex; align-items: center; justify-content: center; padding: 0; border: 1px solid rgba(165, 180, 252, .28); border-right: 0; border-radius: .65rem 0 0 .65rem; background: rgba(15, 18, 38, .94); color: #a5b4fc; cursor: pointer; }
    .widget-toggle:hover { color: #e0e7ff; }
    .widget-toggle svg { transition: transform 300ms ease; }
    .assembly--hidden .widget-toggle svg { transform: rotate(180deg); }
    .panel { width: min(22rem, calc(100vw - 2rem)); padding: 1rem; border: 1px solid rgba(165, 180, 252, .28); border-radius: 1rem; background: rgba(15, 18, 38, .94); box-shadow: 0 1.25rem 4rem rgba(0, 0, 0, .38); backdrop-filter: blur(18px); }
    .eyebrow { margin: 0 0 .3rem; color: #a5b4fc; font-size: .72rem; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
    h2 { margin: 0 0 .75rem; font-size: 1.05rem; line-height: 1.25; }
    .select-wrap { position: relative; margin: .2rem 0 .4rem; }
    .select-wrap::after { content: ""; position: absolute; right: .85rem; top: 50%; width: .5rem; height: .5rem; border-right: 2px solid #a5b4fc; border-bottom: 2px solid #a5b4fc; transform: translateY(-70%) rotate(45deg); pointer-events: none; }
    #draft-target { appearance: none; width: 100%; box-sizing: border-box; padding: .6rem 2.2rem .6rem .8rem; border: 1px solid rgba(165, 180, 252, .28); border-radius: .65rem; background: rgba(129, 140, 248, .1); color: #eef2ff; font: inherit; font-size: .86rem; font-weight: 600; cursor: pointer; transition: border-color 150ms ease, background 150ms ease; }
    #draft-target:hover { border-color: rgba(165, 180, 252, .5); background: rgba(129, 140, 248, .16); }
    #draft-target:focus { outline: none; border-color: #818cf8; box-shadow: 0 0 0 .2rem rgba(129, 140, 248, .18); }
    #draft-target option { background: #11152d; color: #eef2ff; }
    .status { display: flex; align-items: center; gap: .45rem; margin: .55rem 0 .9rem; color: #cbd5e1; font-size: .82rem; }
    .dot { width: .5rem; height: .5rem; border-radius: 50%; background: #64748b; }
    .dot--changed { background: #fbbf24; box-shadow: 0 0 0 .2rem rgba(251, 191, 36, .14); }
    .actions { display: grid; gap: .55rem; }
    button { appearance: none; border: 0; border-radius: .65rem; padding: .72rem .9rem; font: inherit; font-size: .86rem; font-weight: 750; cursor: pointer; transition: transform 150ms ease, opacity 150ms ease, background 150ms ease; }
    button:hover:not(:disabled) { transform: translateY(-1px); }
    button:disabled { cursor: not-allowed; opacity: .46; }
    .primary { color: #111827; background: #fbbf24; }
    .secondary { color: #e0e7ff; background: rgba(129, 140, 248, .17); border: 1px solid rgba(165, 180, 252, .22); }
    .message { margin: .7rem 0 0; color: #86efac; font-size: .8rem; }
    .error { color: #fca5a5; }
    .overlay { position: fixed; inset: 0; z-index: 10001; display: grid; place-items: center; padding: 1rem; background: rgba(2, 6, 23, .72); backdrop-filter: blur(7px); }
    .dialog { width: min(28rem, calc(100vw - 2rem)); padding: 1.35rem; border: 1px solid rgba(165, 180, 252, .25); border-radius: 1rem; background: #11152d; box-shadow: 0 2rem 6rem rgba(0, 0, 0, .5); }
    .dialog h3 { margin: 0 0 .55rem; font-size: 1.2rem; }
    .dialog p { margin: 0 0 1.15rem; color: #cbd5e1; line-height: 1.55; }
    .dialog-actions { display: flex; justify-content: flex-end; gap: .65rem; }
    .cancel { color: #e2e8f0; background: #29304f; }
    @media (max-width: 540px) { :host { right: .65rem; bottom: .65rem; } .panel { width: calc(100vw - 1.3rem); box-sizing: border-box; } .dialog-actions { flex-direction: column-reverse; } }
    @media (prefers-reduced-motion: reduce) { button { transition: none; } }
  `;

  constructor() {
    super();
    this.slug = 'home';
    this.pageId = null;
    this.pageTitle = 'Home';
    this.pageChanged = false;
    this.settingsChanged = false;
    this.pageReady = false;
    this._confirmTarget = '';
    this._busy = false;
    this._message = '';
    this._error = '';
    this._sitemap = [];
    this._history = [];
    this._target = 'page';
    this._hidden = sessionStorage.getItem('siteDraftPanelHidden') === '1';
    this._templateStatus = { changed_count: 0 };
    this._onKeydown = (event) => {
      if (event.key === 'Escape' && !this._busy) this._closeConfirmation();
    };
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keydown', this._onKeydown);
    this._loadBackoffice();
  }

  updated(changed) {
    if (changed.has('pageId') && this.pageId) this._loadBackoffice();
  }

  async _loadBackoffice() {
    try {
      [this._sitemap, this._templateStatus] = await Promise.all([fetchDraftSitemap(), fetchTemplateStatus()]);
      this._history = this.pageId ? await fetchPageHistory(this.pageId) : [];
    } catch (error) {
      this._error = error?.message || 'Could not load draft management data.';
    }
  }

  _flatten(nodes, depth = 0) {
    return nodes.flatMap(node => [{ ...node, depth }, ...this._flatten(node.children || [], depth + 1)]);
  }

  async _selectTarget(event) {
    const value = event.target.value;
    if (value === 'site') {
      this._target = 'site';
      this._history = await fetchSiteHistory();
      return;
    }
    if (value === 'templates') {
      this._target = 'templates';
      this._history = await fetchTemplateHistory();
      return;
    }
    this._target = 'page';
    const page = this._flatten(this._sitemap).find(item => String(item.id) === value);
    const slug = page?.draft_slug || page?.slug;
    if (slug) window.location.assign(withBuildPrefix(`/drafts/${slug}/`));
  }

  async _resetDraft() {
    if (!window.confirm('Reset this draft to the current published version?')) return;
    if (this._target === 'site') await resetSiteDraft();
    else if (this._target === 'templates') await resetTemplateDrafts();
    else if (this.pageId) await resetPageDraft(this.pageId);
    else return;
    window.location.reload();
  }

  async _useVersion(versionId) {
    if (this._target === 'site') await restoreSiteVersion(versionId);
    else if (this._target === 'templates') await restoreTemplateRelease(versionId);
    else await restorePageVersion(this.pageId, versionId);
    window.location.reload();
  }

  async _republish(versionId) {
    if (!window.confirm('Publish this historical version as a new publication?')) return;
    if (this._target === 'site') await republishSiteVersion(versionId);
    else if (this._target === 'templates') await republishTemplateRelease(versionId);
    else await republishPageVersion(this.pageId, versionId);
    await this._loadBackoffice();
    this._message = 'Historical version published as a new version.';
  }

  disconnectedCallback() {
    window.removeEventListener('keydown', this._onKeydown);
    super.disconnectedCallback();
  }

  _toggleHidden() {
    this._hidden = !this._hidden;
    sessionStorage.setItem('siteDraftPanelHidden', this._hidden ? '1' : '0');
  }

  _openConfirmation(target) {
    this._message = '';
    this._error = '';
    this._confirmTarget = target;
  }

  _closeConfirmation() {
    if (!this._busy) this._confirmTarget = '';
  }

  async _publish() {
    const target = this._confirmTarget;
    if (!target || this._busy) return;
    this._busy = true;
    this._error = '';

    try {
      if (target === 'page') {
        await publishDraftPage(this.slug);
        this.pageChanged = false;
        this._message = `${this.pageTitle || this.slug} is now live.`;
      } else {
        if (target === 'templates') {
          await publishTemplateDrafts();
          this._templateStatus = { ...this._templateStatus, changed_count: 0 };
          this._message = 'HTML/CSS templates are now live as one atomic release.';
        } else {
          await publishDraftSiteSettings();
          this.settingsChanged = false;
          this._message = 'Site settings are now live.';
        }
      }
      this.dispatchEvent(new CustomEvent('draft-published', {
        detail: { target, slug: this.slug },
        bubbles: true,
        composed: true,
      }));
      this._confirmTarget = '';
    } catch (error) {
      this._error = error?.message || 'Publishing failed.';
    } finally {
      this._busy = false;
    }
  }

  render() {
    const templateChanged = Number(this._templateStatus.changed_count) > 0;
    const anyChanges = this.pageChanged || this.settingsChanged || templateChanged;
    const label = this.pageTitle || this.slug || 'Page';
    const targetLabel = this._confirmTarget === 'site' ? 'site settings' : this._confirmTarget === 'templates' ? 'HTML/CSS templates' : label;

    return html`
      <div class="assembly ${this._hidden ? 'assembly--hidden' : ''}">
      <button class="widget-toggle" @click=${this._toggleHidden}
              aria-label=${this._hidden ? 'Show publish widget' : 'Hide publish widget'}
              aria-expanded=${this._hidden ? 'false' : 'true'}
              title=${this._hidden ? 'Show publish widget' : 'Hide publish widget'}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
      </button>
      <aside class="panel" aria-label="Draft publishing controls" aria-hidden=${this._hidden ? 'true' : 'false'}>
        <p class="eyebrow">Draft preview</p>
        <h2>${label}</h2>
        <label class="eyebrow" for="draft-target">Sitemap</label>
        <div class="select-wrap">
        <select id="draft-target" @change=${this._selectTarget}>
          ${this._flatten(this._sitemap).map(item => html`<option value=${item.id} ?selected=${item.id === 'site' ? this._target === 'site' : this._target === 'page' && String(item.id) === String(this.pageId)}>${'— '.repeat(item.depth || 0)}${item.draft_title || item.title}</option>`)}
          <option value="templates" ?selected=${this._target === 'templates'}>HTML/CSS templates</option>
        </select>
        </div>
        <div class="status">
          <span class="dot ${anyChanges ? 'dot--changed' : ''}"></span>
          ${!this.pageReady ? 'Preview unavailable' : anyChanges ? 'Unpublished changes' : 'Everything here is published'}
        </div>
        <div class="actions">
          <a class="secondary" href=${withBuildPrefix(`/backoffice/pages/${this.pageId || ''}`)} style="text-align:center;text-decoration:none;padding:.72rem .9rem;border-radius:.65rem">Edit website</a>
          <button class="primary" ?disabled=${!this.pageReady || !this.pageChanged || this._busy} @click=${() => this._openConfirmation('page')}>
            Publish ${label}
          </button>
          ${this.settingsChanged ? html`
            <button class="secondary" ?disabled=${this._busy} @click=${() => this._openConfirmation('site')}>
              Publish site settings
            </button>
          ` : ''}
          ${templateChanged ? html`
            <button class="secondary" ?disabled=${this._busy} @click=${() => this._openConfirmation('templates')}>
              Publish HTML/CSS templates
            </button>
          ` : ''}
          <button class="secondary" ?disabled=${this._target === 'page' && !this.pageId || this._busy} @click=${this._resetDraft}>Reset draft to current publication</button>
        </div>
        <details>
          <summary>Previous publications (${this._history.length})</summary>
          ${this._history.map(version => html`<div class="status">
            <span>v${version.version_number} · ${version.published_at}</span>
            <button class="secondary" @click=${() => this._useVersion(version.id)}>Use as draft</button>
            <button class="primary" @click=${() => this._republish(version.id)}>Publish this version</button>
          </div>`)}
        </details>
        ${this._message ? html`<p class="message" role="status">${this._message}</p>` : ''}
        ${this._error && !this._confirmTarget ? html`<p class="message error" role="alert">${this._error}</p>` : ''}
      </aside>
      </div>

      ${this._confirmTarget ? html`
        <div class="overlay" role="presentation" @click=${(event) => { if (event.target === event.currentTarget) this._closeConfirmation(); }}>
          <section class="dialog" role="dialog" aria-modal="true" aria-labelledby="publish-title">
            <h3 id="publish-title">Publish ${targetLabel}?</h3>
            <p>This immediately replaces the public version with the draft you are previewing. Publishing is only available here, outside MCP.</p>
            ${this._error ? html`<p class="message error" role="alert">${this._error}</p>` : ''}
            <div class="dialog-actions">
              <button class="cancel" ?disabled=${this._busy} @click=${this._closeConfirmation}>Cancel</button>
              <button class="primary" ?disabled=${this._busy} @click=${this._publish}>
                ${this._busy ? 'Publishing…' : `Publish ${targetLabel}`}
              </button>
            </div>
          </section>
        </div>
      ` : ''}
    `;
  }
}

customElements.define('site-draft-panel', SiteDraftPanel);
